import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, Heart, Globe } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { useLang } from "@/hooks/use-language";

export const Route = createFileRoute("/story")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/story" },
      { title: "Our Story | قصتنا — Jozori" },
      {
        name: "description",
        content:
          "How Jozori was born: a story of identity, language, and a generation of Arab children growing up far from home.",
      },
      { property: "og:title", content: "Our Story | قصتنا" },
      {
        property: "og:description",
        content:
          "The human story behind Jozori — built for Arab families abroad who refuse to let their children lose their roots.",
      },
      { property: "og:type", content: "article" },
      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/story" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Our Story — How Jozori Was Born",
          description:
            "How Jozori was born: a story of identity, language, and a generation of Arab children growing up far from home.",
          author: { "@type": "Organization", name: "Jozori" },
          publisher: {
            "@type": "Organization",
            name: "Jozori",
            logo: { "@type": "ImageObject", url: "https://jozori.com/icon-512.png" },
          },
          image: "https://jozori.com/og-image.jpg",
          mainEntityOfPage: "https://jozori.com/story",
        }),
      },
    ],
  }),
  component: StoryPage,
});

const COPY = {
  en: {
    eyebrow: "Our Story",
    title: "A story that began with one quiet worry.",
    lede: "Jozori didn't start in a boardroom. It started at a kitchen table, in a country far from home, when a parent realized their child was forgetting the language of their grandparents.",
    chapters: [
      {
        icon: Globe,
        title: "Born across borders",
        body: "We grew up between cultures, between languages, between airports. We saw firsthand how easy it is for children abroad to drift away from Arabic — not because they don't love it, but because the world around them speaks something else.",
      },
      {
        icon: Heart,
        title: "A promise to the next generation",
        body: "We refused to accept that being raised abroad meant being raised without roots. So we set out to build something that feels like home — joyful, warm, and unmistakably ours.",
      },
      {
        icon: Sparkles,
        title: "From idea to platform",
        body: "Jozori is the result of years of conversations with parents, educators, and children — distilled into stories, lessons, and experiences that make Arabic feel alive again.",
      },
    ],
    outro:
      "Today, Jozori is more than an app. It's a quiet movement of Arab families worldwide choosing to raise children who know exactly who they are.",
    cta: "Read our vision",
  },
  ar: {
    eyebrow: "قصتنا",
    title: "قصة بدأت من قلق هادئ في صدر أم.",
    lede: "لم تبدأ «جذوري» في قاعة اجتماعات، بل بدأت على طاولة مطبخ، في بلد بعيد عن الوطن، حين أدرك أحد الآباء أن طفله بدأ ينسى لغة جدّيه.",
    chapters: [
      {
        icon: Globe,
        title: "وُلدنا بين الحدود",
        body: "كبرنا بين ثقافات ولغات ومطارات، وعشنا بأنفسنا كيف يبتعد أطفال المهجر عن العربية بهدوء — لا لأنهم لا يحبّونها، بل لأن العالم من حولهم يتحدّث لغة أخرى.",
      },
      {
        icon: Heart,
        title: "وعد للجيل القادم",
        body: "رفضنا أن يكون نشوء الطفل في الغربة يعني نشوءه بلا جذور. فقرّرنا أن نبني شيئًا يشبه البيت — دافئًا، مفرحًا، وعربيًّا حتى النخاع.",
      },
      {
        icon: Sparkles,
        title: "من فكرة إلى منصة",
        body: "«جذوري» ثمرة سنوات من الحوار مع الأهل والمعلّمين والأطفال أنفسهم، تحوّلت إلى قصص ودروس وتجارب تُعيد للغة العربية حياتها.",
      },
    ],
    outro:
      "اليوم، «جذوري» ليست مجرّد تطبيق، بل حركة هادئة من عائلات عربية حول العالم اختارت أن تُنشئ أطفالًا يعرفون تمامًا من هم.",
    cta: "اقرأ رؤيتنا",
  },
} as const;

function StoryPage() {
  const { lang } = useLang();
  const t = COPY[lang];
  const isAr = lang === "ar";
  return (
    <SiteShell active="/story">
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10"
        />
        <div className="relative mx-auto max-w-3xl px-4 py-20 md:py-28 text-center">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {t.eyebrow}
          </p>
          <h1
            className={`text-4xl md:text-6xl font-black leading-tight mb-8 ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {t.title}
          </h1>
          <p className="text-lg md:text-xl text-foreground/75 leading-relaxed">{t.lede}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 space-y-10">
        {t.chapters.map((c, i) => {
          const Icon = c.icon;
          return (
            <article
              key={i}
              className={`grid gap-6 md:grid-cols-[80px_1fr] items-start rounded-3xl border border-clay/40 bg-card p-7 md:p-10 ${
                isAr ? "text-right" : ""
              }`}
            >
              <div className="inline-flex size-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <Icon className="size-7" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-black mb-3">{c.title}</h2>
                <p className="text-foreground/75 leading-[1.85] text-base md:text-lg">{c.body}</p>
              </div>
            </article>
          );
        })}
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p
          className={`text-xl md:text-2xl font-bold leading-relaxed text-foreground/90 ${
            isAr ? "font-display-ar" : "font-display-en"
          }`}
        >
          {t.outro}
        </p>
        <Link
          to="/vision"
          className="mt-10 inline-flex items-center rounded-full bg-primary px-7 py-3.5 font-black text-primary-foreground shadow hover:opacity-90"
        >
          {t.cta}
        </Link>
      </section>
    </SiteShell>
  );
}
